import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Inbox, Mail, Gauge, UserPlus, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const READING_TYPE = {
  anmeldung: "Neue Anmeldung",
  zwischenablesung: "Zwischenablesung",
  abmeldung: "Abmeldung",
};

const formatDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const Card = ({ children, testid }) => (
  <div className="rounded-2xl border border-[#D8D3C5] bg-white p-5" data-testid={testid}>
    {children}
  </div>
);

const Meta = ({ children }) => (
  <p className="font-jbm text-xs tracking-wider uppercase text-[#5A826B]">{children}</p>
);

export default function AdminRequests({ token }) {
  const [tab, setTab] = useState("memberships");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/submissions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(r.data);
    } catch {
      setError("Anfragen konnten nicht geladen werden.");
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const tabs = [
    { key: "memberships", label: "Aufnahmeanträge", icon: UserPlus, count: data?.memberships?.length ?? 0 },
    { key: "messages", label: "Kontakt", icon: Mail, count: data?.messages?.length ?? 0 },
    { key: "readings", label: "Zählerstände", icon: Gauge, count: data?.readings?.length ?? 0 },
  ];

  return (
    <section className="mt-12" data-testid="admin-requests">
      <div className="flex items-center gap-3 mb-5">
        <Inbox size={20} className="text-[#1A382B]" />
        <h2 className="font-display text-xl sm:text-2xl font-semibold text-[#1A382B]">Eingegangene Anfragen</h2>
      </div>

      <div className="mb-6 flex flex-wrap gap-2" data-testid="admin-requests-tabs">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`requests-tab-${t.key}`}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-300 ${
              tab === t.key
                ? "bg-[#1A382B] text-[#F8F7F2]"
                : "border border-[#D8D3C5] bg-white text-[#47544A] hover:border-[#5A826B]"
            }`}
          >
            <t.icon size={14} />
            {t.label}
            <span className={`rounded-full px-2 py-0.5 font-jbm text-xs ${tab === t.key ? "bg-[#D4A359] text-[#1A382B]" : "bg-[#EFECE4] text-[#5A826B]"}`}>
              {t.count}
            </span>
          </button>
        ))}
      </div>

      {error && <p className="rounded-xl bg-[#B93829]/10 px-4 py-3 text-sm font-medium text-[#B93829]" data-testid="requests-error">{error}</p>}
      {!data && !error && (
        <p className="flex items-center gap-2 text-sm text-[#6F7E73]" data-testid="requests-loading">
          <Loader2 size={15} className="animate-spin" /> Anfragen werden geladen …
        </p>
      )}

      {data && tab === "memberships" && (
        <div className="space-y-3" data-testid="requests-memberships-list">
          {data.memberships.length === 0 && <Card testid="requests-memberships-empty"><p className="text-sm text-[#47544A]">Noch keine Aufnahmeanträge eingegangen.</p></Card>}
          {data.memberships.map((m, i) => (
            <Card key={m.id} testid={`request-membership-${i}`}>
              <Meta>{formatDateTime(m.created_at)}{m.parcel ? ` · Parzelle ${m.parcel}` : ""}</Meta>
              <p className="mt-1.5 font-semibold text-[#1A382B]">{m.first_name} {m.last_name}</p>
              <p className="text-sm text-[#47544A]">
                <a href={`mailto:${m.email}`} className="underline decoration-[#C85A32]/50 underline-offset-4 hover:text-[#C85A32]">{m.email}</a>
                {m.phone ? ` · ${m.phone}` : ""}
              </p>
              {m.message && <p className="mt-2 text-sm leading-relaxed text-[#47544A]">„{m.message}“</p>}
            </Card>
          ))}
        </div>
      )}

      {data && tab === "messages" && (
        <div className="space-y-3" data-testid="requests-messages-list">
          {data.messages.length === 0 && <Card testid="requests-messages-empty"><p className="text-sm text-[#47544A]">Noch keine Kontaktnachrichten eingegangen.</p></Card>}
          {data.messages.map((m, i) => (
            <Card key={m.id} testid={`request-message-${i}`}>
              <Meta>{formatDateTime(m.created_at)} · {m.subject}</Meta>
              <p className="mt-1.5 font-semibold text-[#1A382B]">{m.name}</p>
              <p className="text-sm text-[#47544A]">
                <a href={`mailto:${m.email}`} className="underline decoration-[#C85A32]/50 underline-offset-4 hover:text-[#C85A32]">{m.email}</a>
              </p>
              <p className="mt-2 text-sm leading-relaxed text-[#47544A]">„{m.message}“</p>
            </Card>
          ))}
        </div>
      )}

      {data && tab === "readings" && (
        <div className="space-y-3" data-testid="requests-readings-list">
          {data.readings.length === 0 && <Card testid="requests-readings-empty"><p className="text-sm text-[#47544A]">Noch keine Zählerstände übermittelt.</p></Card>}
          {data.readings.map((m, i) => (
            <Card key={m.id} testid={`request-reading-${i}`}>
              <Meta>
                {READING_TYPE[m.reading_type] || m.reading_type} · Ablesung {m.reading_date} · Parzelle {m.parcel}
              </Meta>
              <p className="mt-1.5 font-semibold text-[#1A382B]">
                {m.first_name} {m.last_name} · Zählerstand {m.meter_value} (Zähler {m.meter_number})
              </p>
              <p className="text-sm text-[#47544A]">
                Nutzernummer {m.water_user_number} · {m.street} · {m.phone} ·{" "}
                <a href={`mailto:${m.email}`} className="underline decoration-[#C85A32]/50 underline-offset-4 hover:text-[#C85A32]">{m.email}</a>
              </p>
              {m.note && <p className="mt-2 text-sm leading-relaxed text-[#47544A]">„{m.note}“</p>}
            </Card>
          ))}
        </div>
      )}
    </section>
  );
}
